// A real DOM <input>, parked over the canvas where a pixi box is drawn.
//
// The crafting panel's search box used to read document keydowns and draw its own text, which is
// everything a phone cannot give: a soft keyboard with autocorrect does not produce the keystrokes it
// read, and nothing raises that keyboard in the first place. gui/chat_composer.ts says the same thing
// at greater length -- a real <input> is the only thing that opens a phone's keyboard at all.
//
// It is NOT a pixi-text-input like the composer's. That library parents its DOM node to a display
// object and appends/removes it on 'added'/'removed', and this panel rebuilds its whole tree on every
// server message, scroll and keystroke -- so the node would be torn out and re-appended mid-word, and
// the field would lose focus after a single letter. A node the panel does not own survives all of it;
// each rebuild just tells it where the box ended up.
//
// The box the panel draws is now only a FRAME. The input is visible and carries its own text, so a
// tap lands on the element itself -- a genuine user gesture, which is what a phone wants before it
// will raise a keyboard, rather than a focus() call issued from a pixi handler.

export interface OverlayRect {
    left: number
    top: number
    width: number
    height: number
}

/**
 * Where a pixi rectangle lands on the PAGE.
 *
 * Three coordinate spaces meet here. The renderer is sized to the virtual screen (800x600), the
 * canvas is letterboxed to some other size in CSS pixels, and the page has the canvas somewhere in
 * it. A display object's global position is in the first; a DOM node needs the third.
 *
 * Deliberately takes the canvas's measured rect rather than reading it: that keeps this pure, and the
 * caller has to measure anyway.
 */
export const overlayRect = (
    global: { x: number; y: number; width: number; height: number },
    screen: { width: number; height: number },
    canvas: OverlayRect,
): OverlayRect => {
    const scaleX = screen.width > 0 ? canvas.width / screen.width : 0
    const scaleY = screen.height > 0 ? canvas.height / screen.height : 0
    return {
        left: canvas.left + global.x * scaleX,
        top: canvas.top + global.y * scaleY,
        width: global.width * scaleX,
        height: global.height * scaleY,
    }
}

export interface SearchFieldProps {
    placeholder: string
    ariaLabel: string
    // Every edit, including a paste and the phone's autocorrect rewriting a word behind the player.
    onInput: (value: string) => void
    // The field took the keyboard, by its own tap or a click. The panel has to stop feeding those
    // keystrokes to the player at the same moment, not on the first letter that arrives.
    onFocus: () => void
    // The field gave the keyboard back: Enter, the phone keyboard's "search" key, Escape, or a tap
    // somewhere else on the page. One callback for all of them, because they all mean the same thing
    // and all arrive as a native blur.
    onBlur: () => void
    // Left padding beyond the usual 8px, in multiples of the box's height -- room for a glyph the
    // frame draws at the field's left end.
    leadingInset?: number
}

const SEARCH_FIELD_CLASS = "samsara-search-field"
let placeholderStyled = false

// A placeholder's colour can only be set from a stylesheet (::placeholder has no inline form), so the
// rule is added to the page once, the first time a field is built.
const ensurePlaceholderStyle = () => {
    if (placeholderStyled || typeof document === "undefined") {
        return
    }
    placeholderStyled = true
    const style = document.createElement("style")
    style.textContent = `.${SEARCH_FIELD_CLASS}::placeholder { color: #8f7d62; opacity: 1; }`
    document.head.appendChild(style)
}

export class SearchField {
    private props: SearchFieldProps
    private node?: HTMLInputElement

    constructor(props: SearchFieldProps) {
        this.props = props
    }

    /** Build the node on first use. Idempotent. */
    private ensure(): HTMLInputElement | undefined {
        if (this.node) {
            return this.node
        }
        if (typeof document === "undefined") {
            return undefined
        }
        const node = document.createElement("input")
        node.type = "text"
        node.value = ""
        node.setAttribute("aria-label", this.props.ariaLabel)
        node.placeholder = this.props.placeholder
        // A search box is not prose: the phone's sentence case and autocorrect would fight a player
        // typing "shard-metal". `enterkeyhint` turns the keyboard's return key into "search", which
        // is the one key that means "I am done here" on a device with no Escape.
        node.setAttribute("autocapitalize", "off")
        node.setAttribute("autocomplete", "off")
        node.setAttribute("autocorrect", "off")
        node.setAttribute("spellcheck", "false")
        node.setAttribute("enterkeyhint", "search")

        Object.assign(node.style, {
            position: "fixed",
            margin: "0",
            padding: "0 8px",
            border: "none",
            outline: "none",
            background: "transparent",
            // The camp's ink and gold, to match the timber panel the field is set into.
            color: "#ffedc7",
            caretColor: "#ffd166",
            // 16px is the floor Safari zooms a focused field up to. The viewport meta pins the scale
            // so it should not zoom anyway, but a field already big enough cannot be caught out by a
            // page that loses that pin.
            fontSize: "16px",
            fontFamily: "Arial, sans-serif",
            boxSizing: "border-box",
            // Over the canvas, under anything the browser puts on top of the page.
            zIndex: "40",
            display: "none",
        } as CSSStyleDeclaration)

        node.className = SEARCH_FIELD_CLASS
        ensurePlaceholderStyle()

        node.addEventListener("input", () => this.props.onInput(node.value))
        node.addEventListener("keydown", event => {
            // Both "I am done here" keys actually BLUR the node rather than just telling the panel
            // they were pressed. Updating panel state alone left the <input> holding focus: the
            // caret stayed in it, and -- because of the stopPropagation below -- no number of further
            // Escapes could reach the modal to close the screen. The native blur event is what drives
            // the callback, so there is one path out whether the player used a key or tapped away.
            if (event.key === "Enter" || event.key === "Escape") {
                event.preventDefault()
                node.blur()
                return
            }
            // Movement keys must not reach the game while the player is typing into this. Only while
            // the field still has the keyboard -- the two keys above get out of the way first, so
            // Escape closes the panel on the press after the one that left the field.
            event.stopPropagation()
        })
        node.addEventListener("focus", () => this.props.onFocus())
        node.addEventListener("blur", () => this.props.onBlur())

        document.body.appendChild(node)
        this.node = node
        return node
    }

    /** Put the field over this page rectangle and show it. */
    show(rect: OverlayRect, value: string) {
        const node = this.ensure()
        if (!node) {
            return
        }
        if (node.value !== value) {
            node.value = value
        }
        Object.assign(node.style, {
            display: "block",
            left: `${Math.round(rect.left)}px`,
            top: `${Math.round(rect.top)}px`,
            width: `${Math.round(rect.width)}px`,
            height: `${Math.round(rect.height)}px`,
            // The drawn frame is the box; the text should sit on its centre line whatever the
            // letterbox has done to the panel's scale.
            lineHeight: `${Math.round(rect.height)}px`,
            // Room for whatever the frame draws at its left end (the panel's magnifier), measured in
            // box heights so it follows the letterbox's scale like the box does.
            paddingLeft: `${Math.round(8 + rect.height * (this.props.leadingInset ?? 0))}px`,
        } as CSSStyleDeclaration)
    }

    hide() {
        if (this.node) {
            this.node.style.display = "none"
        }
    }

    focus() {
        this.node?.focus()
    }

    /** Give the keyboard back to the page, without taking the field off it. */
    blurNode() {
        this.node?.blur()
    }

    isFocused(): boolean {
        return !!this.node && typeof document !== "undefined" && document.activeElement === this.node
    }

    value(): string {
        return this.node?.value ?? ""
    }

    /**
     * Take the node off the page.
     *
     * The panel is rebuilt and thrown away constantly; this node is not part of that tree, which is
     * the point of it -- and also why nothing else will ever clean it up. A field left behind is the
     * ghost input that has bitten the login card before.
     */
    dispose() {
        this.node?.remove()
        this.node = undefined
    }
}
